<template>
  <v-card class="user-profile-card mt-7">
    <v-card-title>
      <!-- Wraps rather than splitting into fixed columns, so on a narrow screen the
           download button drops below the heading instead of being squeezed beside it. -->
      <div class="d-flex flex-wrap align-center" style="width: 100%">
        <h3 class="mr-auto">Show Results</h3>
        <v-btn
          outlined
          color="primary"
          class="mt-2 mt-sm-0"
          :disabled="filteredResults.length === 0"
          v-on:click="csvExport()"
        >
          <font-awesome-icon icon="file-download" size="lg" />
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text>
      <v-row v-if="hasResults" class="mb-1" dense>
        <v-col cols="12" sm="4">
          <v-select
            v-model="filterDescription"
            :items="descriptionOptions"
            :disabled="descriptionOptions.length < 2"
            label="Program"
            multiple
            clearable
            dense
            hide-details
            outlined
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="filterCategory"
            :items="categoryOptions"
            :disabled="categoryOptions.length === 0"
            :label="categoryOptions.length ? 'Category' : 'Category (none)'"
            no-data-text="No categories for this program"
            multiple
            clearable
            dense
            hide-details
            outlined
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-row dense>
            <v-col cols="6">
              <DatepickerFormatted
                :date="filterStartDate"
                label="From"
                :min="earliestResultDate"
                :max="latestResultDate"
                v-on:updateDates="filterStartDate = $event.date"
              />
            </v-col>
            <v-col cols="6">
              <DatepickerFormatted
                :date="filterEndDate"
                label="To"
                :min="earliestResultDate"
                :max="latestResultDate"
                v-on:updateDates="filterEndDate = $event.date"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <p v-if="hasResults && isFiltered" class="body-2 grey--text text--darken-1 mb-2">
        Showing {{ filteredResults.length }} of {{ allResults.length }} results.
        <a @click="clearFilters">Clear filters</a>
      </p>

      <v-data-table
        class="show-results-data-table d-none d-sm-block"
        :style="tableStyle"
        :headers="visibleHeaders"
        :items="displayedResults"
        sort-by="showDate"
        :sort-desc="true"
        must-sort
      >
        <template v-slot:item.showDate="{ item }">
          <span class="single-line-cell">
            <localized-date :date="item.showDate" />
          </span>
        </template>
        <template v-slot:item.description="{ item }">
          <span class="result-cell-text">{{ item.description }}</span>
        </template>
        <template v-slot:item.location="{ item }">
          <span class="result-cell-text">{{ item.location }}</span>
        </template>
        <template v-slot:item.showTime="{ item }">
          <span class="result-cell-text">{{ item.showTime }}</span>
        </template>
        <template v-slot:item.distance="{ item }">
          <DistanceDisplay v-if="item.distance != null" :value="item.distance" />
        </template>
        <template v-slot:item.category="{ item }">
          <span class="result-cell-text">{{ item.category }}</span>
        </template>
        <template v-slot:item.division="{ item }">
          <span class="result-cell-text">{{ item.division }}</span>
        </template>
        <template v-slot:item.ptSched="{ item }">
          <span class="result-cell-text">{{ item.ptSched }}</span>
        </template>
        <template v-slot:item.handler="{ item }">
          <span class="result-cell-text">{{ item.handler }}</span>
        </template>
        <template v-slot:item.judge="{ item }">
          <span class="result-cell-text">{{ item.judge }}</span>
        </template>
        <template v-slot:item.isScchMajor="{ item }">
          <span v-if="item.isScchMajor != null">
            {{ item.isScchMajor ? 'Yes' : 'No' }}
          </span>
          <span v-else></span>
        </template>
      </v-data-table>

      <v-data-iterator
        class="d-sm-none"
        :items="displayedResults"
        :items-per-page="5"
        sort-by="showDate"
        :sort-desc="true"
        :footer-props="{ itemsPerPageOptions: [5, 10, 25] }"
      >
        <template v-slot:default="{ items }">
          <v-card
            v-for="(item, index) in items"
            :key="`${item.showDate}-${item.description}-${index}`"
            class="mobile-result-card mb-3"
            outlined
          >
            <v-card-title class="subtitle-1 pb-1">
              <localized-date :date="item.showDate" />
            </v-card-title>
            <v-card-subtitle v-if="item.description" class="pt-1 pb-2">
              {{ item.description }}
            </v-card-subtitle>
            <v-card-text>
              <div
                v-for="header in mobileDetailHeadersFor(item)"
                :key="header.value"
                class="mobile-result-row"
              >
                <strong>{{ header.text }}</strong>
                <DistanceDisplay
                  v-if="header.value === 'distance'"
                  :value="item.distance"
                />
                <span v-else-if="header.value === 'isScchMajor'">
                  {{ item.isScchMajor ? 'Yes' : 'No' }}
                </span>
                <span v-else>{{ item[header.value] }}</span>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-data-iterator>
    </v-card-text>        
  </v-card>
</template>

<script lang="ts">
import Vue from "vue";
import DatepickerFormatted from "@/components/common/inputs/DatepickerFormatted.vue";
import { getUserToday } from "@/helpers/date-utils";
import DistanceDisplay from "@/components/common/DistanceDisplay.vue";
import { downloadCsv } from "@/helpers/download-csv";

export default Vue.extend({
  name: "ShowResultsTable",
  components: { DatepickerFormatted, DistanceDisplay },
  props: {
    results: { type: Array, default: () => [] },
    dogId: { type: String, default: "" },
  },
  data: function () {
    return {
      filterDescription: [] as string[],
      filterCategory: [] as string[],
      filterStartDate: null as Temporal.PlainDate | null,
      filterEndDate: null as Temporal.PlainDate | null,
      headers: [
        {
          text: "Show Date",
          align: "left",
          value: "showDate",
        },
        {
          text: "Program",
          align: "left",
          value: "description",
        },
        {
          text: "Location",
          align: "left",
          value: "location",
        },
        { text: "Time", align: "left", value: "showTime" },
        { text: "Distance", align: "left", value: "distance" },
        { text: "Category", align: "left", value: "category" },
        { text: "Division", align: "left", value: "division" },
        { text: "Score", align: "left", value: "score" },
        { text: "Place", align: "left", value: "place" },
        { text: "Hit", align: "left", value: "hit" },
        { text: "PT Sched", align: "left", value: "ptSched" },
        { text: "# Competing", align: "left", value: "nCompeting" },
        { text: "SCCH Score", align: "left", value: "scchScore" },
        { text: "SCCH Major", align: "left", value: "isScchMajor" },
        {
          text: "Handler",
          align: "left",
          value: "handler",
        },
        {
          text: "Judge",
          align: "left",
          value: "judge",
        },
      ],
    };
  },
  computed: {
    allResults(): any[] {
      return (this.$props.results as any[]) ?? [];
    },
    hasResults(): boolean {
      return this.allResults.length > 0;
    },
    tableStyle(): Record<string, string | number> {
      return { "--result-column-count": Math.max(this.visibleHeaders.length, 1) };
    },
    visibleHeaders(): any[] {
      return this.$data.headers.filter((header: any) =>
        this.filteredResults.some((row: any) => {
          const value = header.value === "location" ? this.formatLocation(row) : row[header.value];

          return (
            value != null &&
            (typeof value !== "string" || value.trim() !== "")
          );
        })
      );
    },
    mobileDetailHeaders(): any[] {
      return this.visibleHeaders.filter((header: any) =>
        header.value !== "showDate" && header.value !== "description"
      );
    },
    // Offer only the values this dog actually has, so no filter comes up empty.
    descriptionOptions(): string[] {
      return this.distinctValues(this.allResults, "description");
    },
    // Categories belong to a program, so narrow them once a program is chosen.
    categoryOptions(): string[] {
      const scope = this.allResults.filter((row: any) =>
        this.matches(this.$data.filterDescription, row.description)
      );

      return this.distinctValues(scope, "category");
    },
    resultDays(): string[] {
      return this.allResults
        .map((row: any) => this.dayOf(row))
        .filter((day: string) => day !== "")
        .sort();
    },
    earliestResultDate(): string {
      return this.resultDays.length ? this.resultDays[0] : "";
    },
    latestResultDate(): string {
      return this.resultDays.length ? this.resultDays[this.resultDays.length - 1] : "";
    },
    // The picker reports the two dates in click order, so read them low to high.
    dateRange(): string[] {
      return this.$data.filterStartDate && this.$data.filterEndDate
        ? [this.$data.filterStartDate.toString(), this.$data.filterEndDate.toString()].sort()
        : [];
    },
    isFiltered(): boolean {
      return (
        this.$data.filterDescription.length > 0 ||
        this.$data.filterCategory.length > 0 ||
        this.dateRange.length === 2
      );
    },
    filteredResults(): any[] {
      if (!this.isFiltered) return this.allResults;

      return this.allResults.filter(
        (row: any) =>
          this.matches(this.$data.filterDescription, row.description) &&
          this.matches(this.$data.filterCategory, row.category) &&
          this.withinDateRange(row)
      );
    },
    displayedResults(): any[] {
      return this.filteredResults.map((row: any) => ({
        ...row,
        location: this.formatLocation(row),
      }));
    },
  },
  methods: {
    hasDisplayValue: function (value: any): boolean {
      return value != null && (typeof value !== "string" || value.trim() !== "");
    },
    mobileDetailHeadersFor: function (item: any): any[] {
      return this.mobileDetailHeaders.filter((header: any) =>
        this.hasDisplayValue(item[header.value])
      );
    },
    formatLocation: function (row: any): string {
      const city = (row.city ?? "")
        .trim()
        .replace(/(?:,\s*|\s+)\d[\d\s-]*$/, "")
        .trim();
      const country = (row.country ?? "").trim();
      const domestic = ["usa", "united states"].indexOf(country.toLowerCase()) >= 0;

      return [city, row.state, domestic ? "" : country]
        .map((part: string) => (part ?? "").trim())
        .filter((part: string) => part !== "")
        .join(", ");
    },
    // Stored values carry padding, so compare and display them trimmed.
    distinctValues: function (rows: any[], field: string): string[] {
      const values = rows
        .map((row: any) => (row[field] ?? "").trim())
        .filter((value: string) => value !== "");

      return Array.from(new Set(values)).sort();
    },
    matches: function (selected: string[], value: string): boolean {
      return selected.length === 0 || selected.indexOf((value ?? "").trim()) >= 0;
    },
    dayOf: function (row: any): string {
      return row.showDate?.toString() ?? "";
    },
    withinDateRange: function (row: any): boolean {
      if (this.dateRange.length !== 2) return true;

      const day = this.dayOf(row);

      return day !== "" && day >= this.dateRange[0] && day <= this.dateRange[1];
    },
    clearFilters: function () {
      this.filterDescription = [];
      this.filterCategory = [];
      this.filterStartDate = null;
      this.filterEndDate = null;
    },
    csvExport: function () {
      const filename = `ShowResults_${this.$props.dogId?.trim()}_${getUserToday()}.csv`;
      downloadCsv(filename, this.filteredResults);
    },
  },
  watch: {
    // A category the chosen program does not contain would filter everything out.
    filterDescription: function () {
      const allowed = this.categoryOptions;
      this.filterCategory = this.filterCategory.filter(
        (chosen: string) => allowed.indexOf(chosen) >= 0
      );
    },
  },
});
</script>

<style scoped>
@media (min-width: 600px) {
::v-deep .show-results-data-table > .v-data-table__wrapper > table {
  display: block;
  min-width: calc(var(--result-column-count) * 100px);
  width: 100%;
}

::v-deep .show-results-data-table > .v-data-table__wrapper > table > thead,
::v-deep .show-results-data-table > .v-data-table__wrapper > table > tbody {
  display: block;
}

::v-deep .show-results-data-table > .v-data-table__wrapper > table > thead > tr,
::v-deep .show-results-data-table > .v-data-table__wrapper > table > tbody > tr {
  display: grid;
  grid-template-columns: repeat(var(--result-column-count), minmax(100px, 1fr));
}

::v-deep .show-results-data-table .v-data-table__empty-wrapper > td {
  grid-column: 1 / -1;
}

::v-deep .show-results-data-table > .v-data-table__wrapper > table > thead > tr > th,
::v-deep .show-results-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding-left: 8px;
  padding-right: 8px;
}

::v-deep .show-results-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  overflow: hidden;
  white-space: normal;
}

::v-deep .show-results-data-table > .v-data-table__wrapper > table > thead > tr > th {
  vertical-align: bottom;
  white-space: nowrap;
}
}

.result-cell-text {
  display: -webkit-box;
  overflow: hidden;
  line-height: 1.25rem;
  overflow-wrap: normal;
  white-space: normal;
  word-break: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.single-line-cell {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mobile-result-row {
  display: grid;
  grid-template-columns: minmax(100px, 35%) minmax(0, 1fr);
  gap: 12px;
  padding: 6px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  line-height: 1.35;
  overflow-wrap: anywhere;
}
</style>
